import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
const FIELDS = [{
  key: 'dailyUser',
  label: 'Per user, per day',
  help: 'Stops one person running up a bill'
}, {
  key: 'dailyCompany',
  label: 'Per company, per day',
  help: 'Stops one account running up a bill'
}, {
  key: 'dailyGlobal',
  label: 'Everyone, per day',
  help: 'The ceiling no company can spend past'
}];

/** Ceilings are clamped server side, so the worst a wrong number here can do is be ignored. */
export function LimitsTab() {
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    data = _useState2[0],
    setData = _useState2[1];
  const _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    draft = _useState4[0],
    setDraft = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    status = _useState6[0],
    setStatus = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    busy = _useState8[0],
    setBusy = _useState8[1];
  const load = () => livyJson('/settings').then(d => {
    setData(d);
    setDraft(Object.fromEntries(FIELDS.map(f => {
      var _d$settings$f$key;
      return [f.key, String((_d$settings$f$key = d.settings[f.key]) !== null && _d$settings$f$key !== void 0 ? _d$settings$f$key : '')];
    })));
  }).catch(e => setStatus({
    kind: 'error',
    text: e.message
  }));
  useEffect(() => {
    void load();
  }, []);
  const save = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const body = Object.fromEntries(FIELDS.map(f => [f.key, Number(draft[f.key])]));
      const _await$livyJson = await livyJson('/settings', {
          method: 'PUT',
          body
        }),
        settings = _await$livyJson.settings;
      setStatus({
        kind: 'ok',
        text: 'Saved. It applies to the next turn, no deploy needed.'
      });
      setDraft(Object.fromEntries(FIELDS.map(f => [f.key, String(settings[f.key])])));
      await load();
    } catch (e) {
      setStatus({
        kind: 'error',
        text: e.message
      });
    } finally {
      setBusy(false);
    }
  };
  if (!data) return /*#__PURE__*/React.createElement(Stack, {
    spacing: "8px",
    sx: {
      mt: '16px'
    }
  }, [0, 1, 2].map(i => /*#__PURE__*/React.createElement(Skeleton, {
    key: i,
    height: 56
  })));
  const used = data.spend.global;
  const ceiling = data.settings.dailyGlobal;
  const pct = ceiling ? Math.min(100, used / ceiling * 100) : 0;
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      pt: '16px',
      maxWidth: 560
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mb: '18px'
    }
  }, "Spend ceilings for Livy. A change applies to the next turn rather than the next deploy. Each one is clamped in code, so a value above the maximum is reduced rather than accepted."), /*#__PURE__*/React.createElement(Box, {
    sx: {
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px',
      p: '14px',
      mb: '20px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    justifyContent: "space-between",
    sx: {
      mb: '6px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      fontWeight: 600
    }
  }, "Today, everyone"), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      fontVariantNumeric: 'tabular-nums'
    }
  }, "$", used.toFixed(2), " of $", ceiling)), /*#__PURE__*/React.createElement(LinearProgress, {
    variant: "determinate",
    value: pct,
    color: pct > 80 ? 'warning' : 'primary',
    sx: {
      height: 6,
      borderRadius: 3
    }
  })), status && /*#__PURE__*/React.createElement(Alert, {
    severity: status.kind === 'ok' ? 'success' : 'error',
    sx: {
      mb: '14px'
    }
  }, status.text), /*#__PURE__*/React.createElement(Stack, {
    spacing: "14px"
  }, FIELDS.map(f => {
    var _draft$f$key;
    return /*#__PURE__*/React.createElement(TextField, {
      key: f.key,
      label: f.label,
      size: "small",
      type: "number",
      value: (_draft$f$key = draft[f.key]) !== null && _draft$f$key !== void 0 ? _draft$f$key : '',
      onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
        [f.key]: e.target.value
      })),
      helperText: "".concat(f.help, ". Maximum ").concat(data.maximums[f.key], "."),
      InputProps: {
        startAdornment: /*#__PURE__*/React.createElement(Box, {
          sx: {
            mr: '6px',
            color: 'text.secondary'
          }
        }, "$")
      }
    });
  })), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "10px",
    alignItems: "center",
    sx: {
      mt: '16px'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: save,
    disabled: busy,
    sx: {
      textTransform: 'none'
    }
  }, "Save limits"), data.settings.updatedAt && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11.5,
      color: 'text.secondary'
    }
  }, "Last changed ", new Date(data.settings.updatedAt).toLocaleString())));
}